import React, { useMemo, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import nativeLanguageNames from "@/constants/nativeLanguageNames";
import onboardingCollections from "@/constants/onboardingCollections";
import { LEARNING_LANGUAGE_OPTIONS } from "@/constants/langcraftLanguages";

function OnboardingAlertDialog({ open, setOpen, onStart }) {
  const [selectedNativeLanguage, setSelectedNativeLanguage] = useState("en");
  const [selectedLanguage, setSelectedLanguage] = useState("en");
  const [selectedType, setSelectedType] = useState("basic");
  const [step, setStep] = useState(1);

  const stepDetails = useMemo(
    () => ({
      1: {
        heading: "Select your native language",
        helper:
          "We'll tune translations and pronunciation tips so they feel natural for you.",
      },
      2: {
        heading: "Choose a sample collection",
        helper:
          "Start with a curated set of cards - you can always create your own later.",
      },
    }),
    []
  );

  const handleNativeLanguageChange = (value) => {
    setSelectedNativeLanguage(value);
    setStep(2);
  };

  const handleLanguageChange = (value) => {
    setSelectedLanguage(value);
  };

  const handleTypeChange = (value) => {
    setSelectedType(value);
  };

  const handleStart = () => {
    onStart(selectedNativeLanguage, selectedLanguage, selectedType);
    setOpen(false);
  };

  const handleNext = () => {
    if (selectedNativeLanguage) {
      setStep(2);
    }
  };

  const handleBack = () => setStep(1);

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogContent className="max-w-xl rounded-[32px] border border-line bg-surface p-8 text-fg shadow-panel backdrop-blur-3xl">
        <AlertDialogHeader className="space-y-4 text-left">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.45em] text-fg-muted">
            <span className="h-1 w-1 rounded-full bg-primary" />
            Onboarding
          </span>
          <AlertDialogTitle className="text-3xl font-semibold text-fg">
            Welcome to SpeechCraft!
          </AlertDialogTitle>
          <AlertDialogDescription className="text-base leading-relaxed text-fg-muted">
            {stepDetails[step].helper}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="mt-6 space-y-5">
          <div className="inline-flex items-center gap-2 rounded-full border border-line bg-elevated px-4 py-1 text-sm font-medium text-primary/80 shadow-panel">
            <span className="h-2 w-2 rounded-full bg-primary" />
            {`Step ${step} of 2`}
          </div>

          <div className="space-y-3 rounded-3xl border border-line bg-elevated p-6 backdrop-blur-2xl">
            <div className="space-y-1">
              <p className="text-lg font-semibold text-fg">
                {stepDetails[step].heading}
              </p>
              <p className="text-sm text-fg-muted">
                {step === 1
                  ? "Pick the language you're most comfortable with. You can change this anytime in Account."
                  : "Choose a language and difficulty to copy a collection into your library."}
              </p>
            </div>

            {step === 1 && (
              <Select
                value={selectedNativeLanguage}
                defaultValue={selectedNativeLanguage}
                onValueChange={handleNativeLanguageChange}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select native language" />
                </SelectTrigger>
                <SelectContent>
                  {Object.keys(nativeLanguageNames).map((languageCode) => (
                    <SelectItem
                      key={languageCode}
                      value={languageCode.replace(/\_/g, "-")}
                    >
                      {nativeLanguageNames[languageCode]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {step === 2 && (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Select value={selectedLanguage} onValueChange={handleLanguageChange}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Pick the collection language" />
                  </SelectTrigger>
                  <SelectContent>
                    {LEARNING_LANGUAGE_OPTIONS.filter(({ code }) => onboardingCollections[code]).map(
                      ({ code, label, flag, isDialect }) => (
                        <SelectItem key={code} value={code} className={isDialect ? "pl-7" : undefined}>
                          {flag} {label}
                        </SelectItem>
                      )
                    )}
                  </SelectContent>
                </Select>

                <Select value={selectedType} onValueChange={handleTypeChange}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Choose a collection type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="basic">Introductory Phrases</SelectItem>
                    <SelectItem value="tongue-twisters">
                      Tongue Twisters
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>

        <AlertDialogFooter className="mt-8 gap-3 sm:space-x-4">
          {step === 1 && (
            <Button size="lg" onClick={handleNext} className="w-full sm:w-auto">
              Continue
            </Button>
          )}
          {step === 2 && (
            <>
              <Button
                variant="outline"
                size="lg"
                onClick={handleBack}
                className="w-full border-line bg-transparent text-fg hover:border-primary/40 hover:bg-elevated sm:w-auto"
              >
                Back
              </Button>
              <AlertDialogAction
                onClick={handleStart}
                className="h-12 w-full rounded-3xl border border-primary/40 bg-primary/90 px-7 text-base font-semibold text-primary-fg shadow-panel transition hover:border-primary/60 hover:bg-primary sm:w-auto"
              >
                Start practicing
              </AlertDialogAction>
            </>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default OnboardingAlertDialog;
